<template>
  <v-dialog
    v-model="dialog"
    :persistent="!modal.canCloseOutside"
    :max-width="modal.size"
    :content-class="`${modal.withoutShadow ? 'modal--without-shadow' : ''}`"
  >
    <component :is="modal.name" />
  </v-dialog>
</template>

<script>
  /* eslint-disable vue/no-unused-components */
  import { mapGetters, mapActions } from 'vuex'

  // Components
  import SuccessFlashModal from '@/components/modals/SuccessFlashModal'
  import ErrorFlashModal from '@/components/modals/ErrorFlashModal'
  import InternalReviewModal from '@/components/payments/ReviewModal/InternalReviewModal'
  import TopupReviewModal from '@/components/payments/ReviewModal/TopupReviewModal'
  import WithdrawalReviewModal from '@/components/payments/ReviewModal/WithdrawalReviewModal'
  import FeeReviewModal from '@/components/payments/ReviewModal/FeeReviewModal'
  import FreetextAcceptNotificationModal from '@/components/notifications/FreetextAcceptNotificationModal'
  import ForexReviewModal from '@/components/payments/ReviewModal/ForexReviewModal'
  import CustomizeColumnsModal from '@/components/modals/CustomizeColumnsModal'
  import CreateAccountModal from '@/components/accounts/CreateAccountModal'
  import EditAccountModal from '@/components/accounts/EditAccountModal'
  import BlockAccountModal from '@/components/accounts/BlockAccountModal'
  import DebtWriteOffModal from '@/components/accounts/DebtWriteOffModal'
  import EnableAccountModal from '@/components/accounts/EnableAccountModal'
  import MasterAccountFormModal from '@/components/accounts/MasterAccountFormModal'
  import UseAccountForFeeModal from '@/components/accounts/UseAccountForFeeModal'
  import UseAccountForVaultModal from '@/components/accounts/UseAccountForVaultModal.vue'
  import AddAccountNumberModal from '@/components/accounts/AddAccountNumberModal'
  import DeleteAccountNumberModal from '@/components/accounts/DeleteAccountNumberModal'
  import ProfileStatementDownloadModal from '@/components/profiles/ProfileStatementDownloadModal'
  import SessionTimeoutModal from '@/components/authentication/SessionTimeoutModal'
  import SepaOperationCancelModal from '@/components/operations/SepaOperationCancelModal'
  import FeeOperationReturnModal from '@/components/operations/FeeOperationReturnModal'
  import PaymentCancelModal from '@/components/payments/PaymentCancelModal'
  import CardPaymentCancelModal from '@/components/payments/CardPaymentCancelModal'
  import PaymentSwiftTopUpCancelModal from '@/components/payments/PaymentSwiftTopUpCancelModal'
  import PaymentSwiftTopUpCancelDuplicateModal from '@/components/payments/PaymentSwiftTopUpCancelDuplicateModal'
  import PaymentRetryModal from '@/components/payments/PaymentSepaRetryModal'
  import PaymentSwiftRetryModal from '@/components/payments/PaymentSwiftRetryModal'
  import PaymentIfastApiRetryModal from '@/components/payments/PaymentIfastApiRetryModal'
  import PaymentIfastApiReturnToOnManualProcessingModal from '@/components/payments/PaymentIfastApiReturnToOnManualProcessingModal'
  import ClientRequestStatusModal from '@/components/client-requests/ClientRequestStatusModal'
  import ClientRequestAssignModal from '@/components/client-requests/ClientRequestAssignModal'
  import PaymentChannelSetModal from '@/components/payments/PaymentChannelSetModal'
  import PaymentChannelChangeModal from '@/components/payments/PaymentChannelChangeModal'
  import PaymentSettleModal from '@/components/payments/PaymentSettleModal'
  import PaymentCommentUpdateModal from '@/components/payments/PaymentCommentUpdateModal'
  import PaymentIdentifyRecipientModal from '@/components/payments/PaymentIdentifyRecipientModal'
  import PaymentAssignModal from '@/components/payments/PaymentAssignModal'
  import PaymentUnassignModal from '@/components/payments/PaymentUnassignModal'
  import PaymentAcceptModal from '@/components/payments/PaymentAcceptModal'
  import PaymentManualReturnModal from '@/components/payments/PaymentManualReturnModal'
  import PaymentRejectModal from '@/components/payments/PaymentRejectModal'
  import PaymentReturnHoldModal from '@/components/payments/PaymentReturnHoldModal'
  import PaymentSepaReturnModal from '@/components/payments/PaymentSepaReturnModal'
  import EditAmlCommentModal from '@/components/payments/EditAmlCommentModal'
  import CardOrderRegisterModal from '@/components/cards/CardOrderRegisterModal'
  import CardOrderInitializedModal from '@/components/cards/CardOrderInitializedModal'
  import CardOrderUpdateModal from '@/components/cards/CardOrderUpdateModal'
  import CardOrderDeclineModal from '@/components/cards/CardOrderDeclineModal'
  import CardActivateModal from '@/components/cards/CardActivateModal'
  import CardBlockModal from '@/components/cards/CardBlockModal'
  import CardUnblockModal from '@/components/cards/CardUnblockModal'
  import CardOrderCreateModal from '@/components/cards/CardOrderCreateModal'
  import CardPhoneNumberEditModal from '@/components/cards/CardPhoneNumberEditModal'
  import CardOrderUserIdEditModal from '@/components/cards/CardOrderUserIdEditModal'
  import ClientRequestCreateModal from '@/components/client-requests/ClientRequestCreateModal'
  import ProfileFeeDisableModal from '@/components/fees/ProfileFeeDisableModal'
  import ProfileFeeEnableModal from '@/components/fees/ProfileFeeEnableModal'
  import TransactionFeeDeleteModal from '@/components/fees/TransactionFeeDeleteModal'
  import CurrencyConversionPaymentCancelModal from '@/components/payments/CurrencyConversionPaymentCancelModal'
  import SetRecurringFeeFreeModal from '@/components/accounts/SetRecurringFeeFreeModal'
  import UnsetRecurringFeeFreeModal from '@/components/accounts/UnsetRecurringFeeFreeModal'
  import PaymentReturnModal from '@/components/payments/PaymentReturnModal'
  import PaymentForceToSepaModal from '@/components/payments/PaymentForceToSepaModal'
  import PaymentForceToAutoSwiftModal from '@/components/payments/PaymentForceToAutoSwiftModal'
  import PaymentBankSwiftEditModal from '@/components/payments/PaymentBankSwiftEditModal'
  import BankDetailsEditModal from '@/components/payments/BankDetailsEditModal.vue'
  import PaymentOrderCancelModal from '@/components/payment-orders/PaymentOrderCancelModal'
  import DepositDeclineModal from '@/components/deposits/DepositDeclineModal'
  import TkbSwiftsRemoveModal from '@/components/tkb/TkbSwiftsRemoveModal'
  import Target2RemoveModal from '@/components/target2/Target2RemoveModal'
  import RejectSepaReturnRequestModal from '@/components/payments/RejectSepaReturnRequestModal'
  import RequisitesModal from '@/components/accounts/RequisitesModal'
  import RequisitesSendModal from '@/components/accounts/RequisitesSendModal'
  import WalletTypeModal from '@/components/accounts/WalletTypeModal'
  import WalletTypeCreateModal from '@/components/accounts/WalletTypeCreateModal'
  import SwiftMessageOrderCreateModal from '@/components/swift-message-orders/SwiftMessageOrderCreateModal'
  import SwiftMessageOrderCancelModal from '@/components/swift-message-orders/SwiftMessageOrderCancelModal'
  import SavingsAccountTransactionUpdateModal from '@/components/savings-account/SavingsAccountTransactionUpdateModal'
  import SavingsAccountDeclineModal from '@/components/savings-account/SavingsAccountDeclineModal'
  import ProfileCustomerCreateModal from '@/components/profile-customers/ProfileCustomerCreateModal'
  import ProfileCustomerUpdateModal from '@/components/profile-customers/ProfileCustomerUpdateModal'
  import ProfileCustomerAccountNumberFormModal from '@/components/profile-customers/ProfileCustomerAccountNumberFormModal'
  import ProfileCustomerAccountNumberStatusModal from '@/components/profile-customers/ProfileCustomerAccountNumberStatusModal'
  import SendEmailToReferralModal from '@/components/referrals/SendEmailToReferralModal'
  import PaymentAmlTrackingMarkEditModal from '@/components/payments/PaymentAmlTrackingMarkEditModal'
  import PaymentTransferChargeTypeChangeModal from '@/components/payments/PaymentTransferChargeTypeChangeModal'
  import BalanceScreenshotsModal from '@/components/balance-screenshots/BalanceScreenshotsModal'
  import ForexCurrencyPairEditorModal from '@/components/forex/ForexCurrencyPairEditorModal'
  import ForexCurrencyPairDeleteModal from '@/components/forex/ForexCurrencyPairDeleteModal'
  import ProfileForexCurrencyPairEditorModal from '@/components/forex/ProfileForexCurrencyPairEditorModal'
  import ForexCurrencyLimitsEditorModal from '@/components/forex/ForexCurrencyLimitsEditorModal'
  import ProfileForexCurrencyLimitsDeleteModal from '@/components/forex/ProfileForexCurrencyLimitsDeleteModal'
  import DepositRatesEditorModal from '@/components/deposits/DepositRatesEditorModal'
  import SavingsAccountRateEditorModal from '@/components/savings-account/SavingsAccountRateEditorModal'
  import BannerDeleteModal from '@/components/banners/BannerDeleteModal'
  import BannerPreviewModal from '@/components/banners/BannerPreviewModal'
  import CashbackRemoveModal from '@/components/cashback/CashbackRemoveModal'
  import CashbackEditModal from '@/components/cashback/CashbackEditModal'
  import CurrencyRatesEditModal from '@/components/rates/CurrencyRatesEditModal'
  import PaymentForcePostponedAutoSwiftModal from '@/components/payments/PaymentForcePostponedAutoSwiftModal'
  import TierWatchdogRuleEditorModal from '@/components/profiles/TierWatchdogRuleEditorModal'
  import PostponedSwiftPaymentReturnOnHoldModal from '@/components/payments/PostponedSwiftPaymentReturnOnHoldModal'
  import OpenBankingAppEditorModal from '@/components/open-banking/OpenBankingAppEditorModal'
  import OpenBankingAppDeleteModal from '@/components/open-banking/OpenBankingAppDeleteModal'
  import OpenBankingPaymentModal from '@/components/open-banking/OpenBankingPaymentModal'
  import CampaignDeleteModal from '@/components/referral-campaigns/CampaignDeleteModal'
  import SendSepaNonreceiptClaimModal from '@/components/payments/SendSepaNonreceiptClaimModal'
  import SendSepaDateCorrectionClaimModal from '@/components/payments/SendSepaDateCorrectionClaimModal'
  import SendSepaClaimResponseModal from '@/components/payments/SendSepaClaimResponseModal'
  import SubscriptionEditModal from '@/components/subscription/SubscriptionEditModal'
  import SubscriptionDiscountAddModal from '@/components/subscription/SubscriptionDiscountAddModal'
  import CardLimitsReviewModal from '@/components/cards/CardLimitsReviewModal'
  import SubscriptionDiscountDeleteModal from '@/components/subscription/SubscriptionDiscountDeleteModal'
  import CountryGroupsAddModal from '@/components/ui/CountryGroups/CountryGroupsAddModal'
  import CountryGroupsRemoveModal from '@/components/ui/CountryGroups/CountryGroupsRemoveModal'
  import RefundEsimFeeModal from '@/components/payments/RefundEsimFeeModal'
  import LinkEsimFeeModal from '@/components/payments/LinkEsimFeeModal'
  import RestoreAccountNumberModal from '@/components/accounts/RestoreAccountNumberModal.vue'
  import EditAccountNumberCurrenciesModal from '@/components/accounts/EditAccountNumberCurrenciesModal.vue'
  import ChannelRouteRemoveModal from '@/components/channel-setup/ChannelRouteRemoveModal'
  import ProfileRestrictionManageModal from '@/components/profiles/ProfileRestrictionManageModal'

  export default {
    name: 'Modal',
    components: {
      SuccessFlashModal,
      ErrorFlashModal,
      InternalReviewModal,
      TopupReviewModal,
      WithdrawalReviewModal,
      FeeReviewModal,
      FreetextAcceptNotificationModal,
      CustomizeColumnsModal,
      CreateAccountModal,
      EditAccountModal,
      BlockAccountModal,
      DebtWriteOffModal,
      EnableAccountModal,
      MasterAccountFormModal,
      UseAccountForFeeModal,
      UseAccountForVaultModal,
      AddAccountNumberModal,
      DeleteAccountNumberModal,
      RestoreAccountNumberModal,
      EditAccountNumberCurrenciesModal,
      ProfileStatementDownloadModal,
      SessionTimeoutModal,
      SepaOperationCancelModal,
      FeeOperationReturnModal,
      PaymentCancelModal,
      CardPaymentCancelModal,
      PaymentSwiftTopUpCancelModal,
      PaymentSwiftTopUpCancelDuplicateModal,
      CurrencyConversionPaymentCancelModal,
      ClientRequestStatusModal,
      ClientRequestAssignModal,
      ForexReviewModal,
      PaymentChannelSetModal,
      PaymentChannelChangeModal,
      PaymentSettleModal,
      PaymentCommentUpdateModal,
      PaymentIdentifyRecipientModal,
      PaymentAssignModal,
      PaymentUnassignModal,
      PaymentAcceptModal,
      PaymentManualReturnModal,
      PaymentRejectModal,
      PaymentReturnHoldModal,
      PaymentSepaReturnModal,
      EditAmlCommentModal,
      CardOrderRegisterModal,
      CardOrderUpdateModal,
      CardOrderInitializedModal,
      CardOrderDeclineModal,
      CardActivateModal,
      CardBlockModal,
      CardUnblockModal,
      CardOrderCreateModal,
      CardPhoneNumberEditModal,
      CardOrderUserIdEditModal,
      ClientRequestCreateModal,
      ProfileFeeDisableModal,
      ProfileFeeEnableModal,
      TransactionFeeDeleteModal,
      SetRecurringFeeFreeModal,
      UnsetRecurringFeeFreeModal,
      PaymentReturnModal,
      PaymentRetryModal,
      PaymentSwiftRetryModal,
      PaymentIfastApiRetryModal,
      PaymentIfastApiReturnToOnManualProcessingModal,
      PaymentForceToSepaModal,
      PaymentForceToAutoSwiftModal,
      PaymentBankSwiftEditModal,
      BankDetailsEditModal,
      PaymentOrderCancelModal,
      DepositDeclineModal,
      TkbSwiftsRemoveModal,
      Target2RemoveModal,
      RejectSepaReturnRequestModal,
      RequisitesModal,
      RequisitesSendModal,
      WalletTypeModal,
      WalletTypeCreateModal,
      SwiftMessageOrderCreateModal,
      SwiftMessageOrderCancelModal,
      SavingsAccountTransactionUpdateModal,
      SavingsAccountDeclineModal,
      ProfileCustomerCreateModal,
      ProfileCustomerUpdateModal,
      ProfileCustomerAccountNumberFormModal,
      ProfileCustomerAccountNumberStatusModal,
      SendEmailToReferralModal,
      PaymentAmlTrackingMarkEditModal,
      PaymentTransferChargeTypeChangeModal,
      BalanceScreenshotsModal,
      ForexCurrencyPairEditorModal,
      ForexCurrencyPairDeleteModal,
      ProfileForexCurrencyPairEditorModal,
      ForexCurrencyLimitsEditorModal,
      ProfileForexCurrencyLimitsDeleteModal,
      DepositRatesEditorModal,
      SavingsAccountRateEditorModal,
      BannerDeleteModal,
      BannerPreviewModal,
      CashbackRemoveModal,
      CashbackEditModal,
      CurrencyRatesEditModal,
      PaymentForcePostponedAutoSwiftModal,
      TierWatchdogRuleEditorModal,
      PostponedSwiftPaymentReturnOnHoldModal,
      OpenBankingAppEditorModal,
      OpenBankingAppDeleteModal,
      OpenBankingPaymentModal,
      CampaignDeleteModal,
      SendSepaNonreceiptClaimModal,
      SendSepaDateCorrectionClaimModal,
      SendSepaClaimResponseModal,
      SubscriptionEditModal,
      SubscriptionDiscountAddModal,
      SubscriptionDiscountDeleteModal,
      CardLimitsReviewModal,
      CountryGroupsAddModal,
      CountryGroupsRemoveModal,
      RefundEsimFeeModal,
      LinkEsimFeeModal,
      ChannelRouteRemoveModal,
      ProfileRestrictionManageModal
    },
    data () {
      return {
        dialog: false
      }
    },
    computed: {
      ...mapGetters('modal', ['modal'])
    },
    watch: {
      'modal.isVisible': 'toggleModal',
      'dialog': 'checkForOutsideClick'
    },
    methods: {
      ...mapActions('modal', ['closeModal']),
      toggleModal () {
        this.dialog = this.modal.isVisible
      },
      checkForOutsideClick () {
        if (this.modal.canCloseOutside && this.dialog !== this.modal.isVisible) {
          this.closeModal()
        }
      }
    }
  }
</script>

<style lang="scss">
  .modal {
    &--without-shadow {
      box-shadow: none;
    }
  }
</style>
